import { useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import NotificationBell from '@/components/NotificationBell';
import { LanguageSwitcher } from '@/contexts/I18nContext';
import { Menu, X, LayoutDashboard, LogOut, Shield, Heart, Building2, Store, Briefcase, Users, BookOpen, BarChart3, Flame } from 'lucide-react';
import { Button } from '@/components/ui/button';
import VisionAireLogo from '@/components/VisionAireLogo';

export default function Navbar({ variant = 'light' }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const isDark = variant === 'dark';

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const scrollToSection = useCallback((sectionId) => {
        if (location.pathname !== '/') {
            navigate('/', { state: { scrollTo: sectionId } });
        } else {
            const el = document.getElementById(sectionId);
            if (el) el.scrollIntoView({ behavior: 'smooth' });
        }
    }, [location.pathname, navigate]);

    const getDashboardLink = () => {
        if (!user) return '/login';
        if (user.role === 'admin') return '/admin';
        if (user.role === 'agent') return '/agent';
        if (user.role === 'operator') return '/operator';
        return '/dashboard';
    };

    return (
        <nav data-testid="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isDark ? 'glass' : 'glass-light shadow-sm'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    <div
                        onClick={() => {
                            if (user && user.role === 'admin') {
                                navigate('/admin');
                            } else {
                                navigate('/');
                            }
                        }}
                        className="flex items-center gap-3 cursor-pointer"
                        data-testid="nav-logo"
                    >
                        <VisionAireLogo size={40} />
                        <span className={`text-lg font-semibold tracking-[0.08em] uppercase font-['Outfit'] ${isDark ? 'text-amber-400' : 'text-zinc-900'}`}>
                            VisionAire
                        </span>
                    </div>

                    {/* Desktop */}
                    <div className="hidden md:flex items-center gap-4">
                        {!user || user === false ? (
                            <>
                                <button onClick={() => scrollToSection('how-it-works')} className={`text-sm font-medium transition-colors ${isDark ? 'text-zinc-300 hover:text-white' : 'text-zinc-600 hover:text-zinc-900'}`}>
                                    How It Works
                                </button>
                                <button onClick={() => scrollToSection('buyers')} className={`text-sm font-medium transition-colors ${isDark ? 'text-zinc-300 hover:text-white' : 'text-zinc-600 hover:text-zinc-900'}`}>
                                    For Buyers
                                </button>
                                <button onClick={() => scrollToSection('sellers')} className={`text-sm font-medium transition-colors ${isDark ? 'text-zinc-300 hover:text-white' : 'text-zinc-600 hover:text-zinc-900'}`}>
                                    For Sellers
                                </button>
                                <Button variant="ghost" onClick={() => navigate('/marketplace')} data-testid="nav-marketplace-btn"
                                    className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                    <Store className="w-4 h-4 mr-2" /> Marketplace
                                </Button>
                                <Button variant="ghost" onClick={() => navigate('/guide')} data-testid="nav-guide-btn"
                                    className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                    <BookOpen className="w-4 h-4 mr-2" /> Guide
                                </Button>
                                <LanguageSwitcher variant={variant} />
                                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login-btn"
                                    className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                    Log In
                                </Button>
                                <Button onClick={() => navigate('/register')} data-testid="nav-register-btn"
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6">
                                    Get Started
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button variant="ghost" onClick={() => navigate(getDashboardLink())} data-testid="nav-dashboard-btn"
                                    className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                    <LayoutDashboard className="w-4 h-4 mr-2" />
                                    Dashboard
                                </Button>
                                <Button variant="ghost" onClick={() => navigate('/marketplace')} data-testid="nav-marketplace-btn"
                                    className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                    <Store className="w-4 h-4 mr-2" /> Marketplace
                                </Button>
                                {(user.role === 'buyer' || user.role === 'seller') && (
                                    <>
                                        <Button variant="ghost" onClick={() => navigate('/credit-repair')} data-testid="nav-credit-repair-btn"
                                            className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                            <Heart className="w-4 h-4 mr-2" /> Credit Repair
                                        </Button>
                                        <Button variant="ghost" onClick={() => navigate('/mortgage')} data-testid="nav-mortgage-btn"
                                            className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                            <Building2 className="w-4 h-4 mr-2" /> Mortgage
                                        </Button>
                                    </>
                                )}
                                {user.role === 'agent' && (
                                    <Button variant="ghost" onClick={() => navigate('/agent')} data-testid="nav-agent-btn"
                                        className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                        <Briefcase className="w-4 h-4 mr-2" /> My Listings
                                    </Button>
                                )}
                                {user.role === 'operator' && (
                                    <>
                                    <Button variant="ghost" onClick={() => navigate('/operator')} data-testid="nav-operator-btn"
                                        className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                        <Users className="w-4 h-4 mr-2" /> Active Listings
                                    </Button>
                                    <Button variant="ghost" onClick={() => navigate('/opportunities')} data-testid="nav-opp-operator-btn"
                                        className={isDark ? 'text-red-300 hover:bg-white/10' : 'text-red-600'}>
                                        <Flame className="w-4 h-4 mr-2" /> Opportunities
                                    </Button>
                                    </>
                                )}
                                {user.role === 'admin' && (
                                    <>
                                        <Button variant="ghost" onClick={() => navigate('/admin')} data-testid="nav-admin-btn"
                                            className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                            <Shield className="w-4 h-4 mr-2" /> Admin
                                        </Button>
                                        <Button variant="ghost" onClick={() => navigate('/crm')} data-testid="nav-crm-btn"
                                            className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                            CRM
                                        </Button>
                                        <Button variant="ghost" onClick={() => navigate('/analytics')} data-testid="nav-analytics-btn"
                                            className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                            <BarChart3 className="w-4 h-4 mr-2" /> Analytics
                                        </Button>
                                        <Button variant="ghost" onClick={() => navigate('/opportunities')} data-testid="nav-opportunities-btn"
                                            className={isDark ? 'text-red-300 hover:bg-white/10' : 'text-red-600'}>
                                            <Flame className="w-4 h-4 mr-2" /> Opportunities
                                        </Button>
                                    </>
                                )}
                                <Button variant="ghost" onClick={() => navigate('/guide')} data-testid="nav-guide-loggedin-btn"
                                    className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                    <BookOpen className="w-4 h-4 mr-2" /> Guide
                                </Button>
                                <NotificationBell variant={variant} />
                                <LanguageSwitcher variant={variant} />
                                <div className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                                    {user.name}
                                </div>
                                <Button variant="ghost" onClick={handleLogout} data-testid="nav-logout-btn"
                                    className={isDark ? 'text-white hover:bg-white/10' : 'text-zinc-700'}>
                                    <LogOut className="w-4 h-4" />
                                </Button>
                            </>
                        )}
                    </div>

                    {/* Mobile toggle */}
                    <button onClick={() => setOpen(!open)} className="md:hidden" data-testid="nav-mobile-toggle">
                        {open ? <X className={isDark ? 'text-white' : 'text-zinc-900'} /> : <Menu className={isDark ? 'text-white' : 'text-zinc-900'} />}
                    </button>
                </div>

                {/* Mobile menu */}
                {open && (
                    <div className={`md:hidden py-4 space-y-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                        {!user || user === false ? (
                            <>
                                <button onClick={() => { scrollToSection('how-it-works'); setOpen(false); }} className="block py-2 text-sm w-full text-left">How It Works</button>
                                <button onClick={() => { scrollToSection('buyers'); setOpen(false); }} className="block py-2 text-sm w-full text-left">For Buyers</button>
                                <button onClick={() => { scrollToSection('sellers'); setOpen(false); }} className="block py-2 text-sm w-full text-left">For Sellers</button>
                                <button className="block w-full text-left py-2 text-sm" onClick={() => { navigate('/marketplace'); setOpen(false); }}>Marketplace</button>
                                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white mt-2" onClick={() => { navigate('/login'); setOpen(false); }}>Log In</Button>
                            </>
                        ) : (
                            <>
                                <button className="block w-full text-left py-2 text-sm" onClick={() => { navigate(getDashboardLink()); setOpen(false); }}>Dashboard</button>
                                <button className="block w-full text-left py-2 text-sm" onClick={() => { navigate('/marketplace'); setOpen(false); }}>Marketplace</button>
                                {(user.role === 'buyer' || user.role === 'seller') && (
                                    <>
                                        <button className="block w-full text-left py-2 text-sm" onClick={() => { navigate('/credit-repair'); setOpen(false); }}>Credit Repair</button>
                                        <button className="block w-full text-left py-2 text-sm" onClick={() => { navigate('/mortgage'); setOpen(false); }}>Mortgage</button>
                                    </>
                                )}
                                {user.role === 'agent' && <button className="block w-full text-left py-2 text-sm" onClick={() => { navigate('/agent'); setOpen(false); }}>Agent Portal</button>}
                                {user.role === 'operator' && <button className="block w-full text-left py-2 text-sm" onClick={() => { navigate('/operator'); setOpen(false); }}>Active Listings</button>}
                                {user.role === 'admin' && <button className="block w-full text-left py-2 text-sm" onClick={() => { navigate('/crm'); setOpen(false); }}>CRM</button>}
                                <button className="block w-full text-left py-2 text-sm text-red-400" onClick={() => { handleLogout(); setOpen(false); }}>Log Out</button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </nav>
    );
}
